/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {debounceTime, filter, finalize, map, switchMap, tap} from 'rxjs/operators';

import {
  InboxItemType,
  Notification,
  NotificationFromSocket,
  NotificationType,
  PayPeriodContractChange,
  RecentNotification
} from './communications.interface';

import {ToastrService} from 'ngx-toastr';
import {
  FailureEvents,
  MassActionEvent,
  MassActionResult,
  SuccessEvents
} from '../../+mass-data-update/bulk-update/mdu-bulk-update.interfaces';
import {ApiService} from '../../shared/api';
import {NotificationSocketService} from '../../shared/api/notification-socket.service';
import {AuthenticationEvent, AuthenticationService} from '../../shared/authentication';

@Injectable({
  providedIn: 'root'
})
export class CommunicationsService {

  public recentList: RecentNotification[] = [];
  public updateSubject: BehaviorSubject<RecentNotification[]> = new BehaviorSubject<RecentNotification[]>(null);
  public notificationUpdated: Observable<RecentNotification[]> = this.updateSubject.asObservable()
    .pipe(
      filter((data: RecentNotification[]) => !!data)
    );

  public informationUnreadCount = 0;
  public tasksUnreadCount = 0;

  public recentListClicked: Subject<void> = new Subject<void>();
  public payPeriodContractChange: Subject<PayPeriodContractChange[]> = new Subject<PayPeriodContractChange[]>();
  public calculationDone: Subject<NotificationFromSocket<any>> = new Subject<NotificationFromSocket<any>>();
  public communicationChange: Subject<NotificationFromSocket<any>> = new Subject<NotificationFromSocket<any>>();
  public massActionChange: Subject<NotificationFromSocket<MassActionEvent | MassActionResult<SuccessEvents, FailureEvents>>>
    = new Subject<NotificationFromSocket<MassActionEvent>>();
  public accountingCalculationDone: Subject<NotificationFromSocket<any>> = new Subject<NotificationFromSocket<any>>();

  private langChange$: Subscription;

  constructor(private api: ApiService,
              private authenticationService: AuthenticationService,
              private translate: TranslateService,
              private notificationSocketService: NotificationSocketService,
              private toastrService: ToastrService) {

    this.communicationChange
      .pipe(
        debounceTime(3000),
        switchMap(() => this.updateCountOfUnread()),
        switchMap(() => this.updateRecentList())
      )
      .subscribe();

    this.massActionChange
      .subscribe((notificationFromSocket: NotificationFromSocket<MassActionEvent>) => {
        const keys = Object.keys(notificationFromSocket.datas);
        if (keys.length === 0 || !notificationFromSocket.datas[keys[0]]) {
          return;
        }
        const massActionEvent = notificationFromSocket.datas[keys[0]];
        if ((massActionEvent as MassActionResult<SuccessEvents, FailureEvents>).failureList) {
          this.toastrService.success(this.translate.instant('MDU.process_complete'));
        }
      });

    this.notificationSocketService.messages$()
      .subscribe((msg: NotificationFromSocket<any>) => {
        if (msg.notificationType === 'PPC_CHANGE') {
          this.payPeriodContractChange.next(msg.datas ? msg.datas['changes'] : null);
        } else if (msg.notificationType === 'CALCULATION') {
          this.calculationDone.next(msg);
          this.communicationChange.next(msg);
        } else if (msg.notificationType === 'COMMUNICATION' || msg.notificationType === 'BUSINESS_PROCESS') {
          this.communicationChange.next(msg);
        } else if (msg.notificationType === 'MASS_ACTION') {
          this.massActionChange.next(msg);
        } else if (msg.notificationType === 'ACCOUNTING_CALCULATION') {
          this.accountingCalculationDone.next(msg);
        }
      });

    this.authenticationService.authenticationEvents
      .subscribe(result => {
        const state = result.event;
        if (state === AuthenticationEvent.LOGIN_SUCCESSFUL || state === AuthenticationEvent.TOKEN_VERIFIED) {
          this.updateCountOfUnread().subscribe();
          this.updateRecentList().subscribe();
          this.langChange$ = this.translate.onLangChange
            .asObservable()
            .pipe(
              debounceTime(1000),
              switchMap(() => this.updateRecentList())
            ).subscribe();

        } else if (state === AuthenticationEvent.LOGOUT) {
          if (this.langChange$) {
            this.langChange$.unsubscribe();
          }
        }
      });
  }

  public updateRecentInboxItemById(id: string, read: boolean = true): Observable<void> {
    const notification = this.recentList.find((inboxItem: RecentNotification) => inboxItem.id === id);
    if (notification) {
      notification.read = read;
    }
    return this.updateCountOfUnread();
  }

  public updateRecentNotificationsByIds(ids: number[] | null, read: boolean = true): Observable<void> {
    if (ids) {
      this.updateNotificationsReadByIds(ids, read);
    } else {
      this.updateAllNotificationsRead(read);
    }

    return this.updateCountOfUnread();
  }

  private updateNotificationsReadByIds(ids: number[] | null, read: boolean = true) {
    this.recentList.forEach((inboxItem: RecentNotification) => {
      if (inboxItem.type === InboxItemType.NOTIFICATION && ids.includes(Number(inboxItem.id))) {
        inboxItem.read = read;
      }
    });
  }

  private updateAllNotificationsRead(read: boolean = true) {
    this.recentList.forEach((inboxItem: RecentNotification) => {
      if (inboxItem.type === InboxItemType.NOTIFICATION) {
        inboxItem.read = read;
      }
    });
  }

  public updateCountOfUnread(): Observable<void> {
    return this.api.get('/communication/api/v1/inbox/unread-count')
      .pipe(
        tap(response => {
          this.tasksUnreadCount = response.tasksUnreadCount;
          this.informationUnreadCount = response.informationUnreadCount;
        })
      );
  }

  public getNotificationIcon(notification: RecentNotification | Notification) {
    if ((notification as RecentNotification).type === InboxItemType.TASK) {
      return 'fa fa-light fa-tasks task-icon';
    }

    let icon: string;
    switch (notification.notificationType) {
      case NotificationType.CALCULATION:
        icon = 'fa fa-light fa-cogs information-icon';
        break;
      case NotificationType.COMMUNICATION:
        icon = 'fa fa-light fa-volume-low information-icon';
        break;
      default:
        icon = 'fa fa-light fa-bell information-icon';
        break;
    }
    return icon;
  }

  private updateRecentList(): Observable<RecentNotification[]> {
    return this.api.get('/communication/api/v1/inbox/recent?size=4')
      .pipe(
        map(recentList => recentList.map(notification => ({
            ...notification,
            icon: this.getNotificationIcon(notification)
          }))
        ),
        tap((data: RecentNotification[]) => this.recentList = data || []),
        finalize(() => {
          this.updateSubject.next(this.recentList);
        })
      );
  }
}
